// qs-Canvas-Main.vue
<template>
  <div
    ref="canvasContainer"
    class="canvas-container"
    tabindex="0"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useEventListener } from '@vueuse/core'
import Konva from 'konva'

import type { LayerSelectedEvent } from '@/types/qs-type-events'
import type { KonvaEvent, KonvaLayer, KonvaStage } from '@/types/qs-type-konva'
import type { DrawingPath, Layer } from '@/types/qs-types-index'

import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useLayerStore } from '@/stores/qs-store-layers'
import { useSilhouetteStore } from '@/stores/qs-store-silhouette'

import { TaperingEngine } from '@/brushes/engines/qs-brush-engine-tapering'
import { brushRegistry } from '@/brushes/qs-brushes-index'
import { useCanvasEvents } from '@/composables/qs-useCanvasEvents'
import { useCanvasPerformance } from '@/composables/qs-useCanvasPerformance'
import { useDeleteMode } from '@/composables/qs-useDeleteMode'
import { useEventBus } from '@/composables/qs-useEventBus'
import { useLayerPositioning } from '@/composables/qs-useLayerPositioning'
import { useModeManager } from '@/composables/qs-useModeManager'
import { usePanMode } from '@/composables/qs-usePanMode'

const canvasContainer = ref<HTMLDivElement>()
const canvasStore = useCanvasStore()
const layerStore = useLayerStore()
const silhouetteStore = useSilhouetteStore()

const canvasEvents = useCanvasEvents()
const deleteMode = useDeleteMode()
const layerPositioning = useLayerPositioning()
const performance = useCanvasPerformance()
const panMode = usePanMode()
const modeManager = useModeManager()

const isPanMode = computed(() => modeManager.activeMode.value === 'pan')

let stage: KonvaStage | null = null
let reinitTimeout: ReturnType<typeof setTimeout> | null = null
let lastTouchDistance = 0
let wheelHandler: ((e: WheelEvent) => void) | null = null
let eventListenerStops: Array<() => void> = []
let watcherStops: Array<() => void> = []
let eventBusHandler: ((data: LayerSelectedEvent) => void) | null = null

const silhouettePreviewColor = ref<string | null>(null)
const backgroundPreviewColor = ref<string | null>(null)

const initCanvas = () => {
  if (!canvasContainer.value) return

  canvasStore.updateDimensions()

  stage = new Konva.Stage({
    container: canvasContainer.value,
    width: canvasStore.width,
    height: canvasStore.height,
    scale: { x: canvasStore.scale, y: canvasStore.scale },
    x: canvasStore.position.x,
    y: canvasStore.position.y
  })

  canvasStore.setStage(stage)
  initLayers()
  setupEventListeners()

  if (modeManager.activeMode.value !== 'drawing') {
    modeManager.switchToDrawingMode()
  }
}

const initLayers = () => {
  if (!stage) return

  stage.destroyChildren()
  updateCSSBackground()
  createSilhouetteLayer()

  const layersForRendering = [...layerStore.layersInOrder].reverse()
  layersForRendering.forEach(layer => {
    createKonvaLayer(layer)
  })

  canvasEvents.restoreTextObjects(stage)
  stage.batchDraw()
}

const updateCSSBackground = () => {
  if (!canvasContainer.value) return

  const colorToUse = backgroundPreviewColor.value || canvasStore.backgroundColor

  canvasContainer.value.style.backgroundColor = colorToUse
  canvasContainer.value.style.backgroundSize = 'cover'
  canvasContainer.value.style.backgroundRepeat = 'no-repeat'
  canvasContainer.value.style.backgroundPosition = 'center'
}

const createKonvaLayer = (layer: Layer) => {
  if (!stage) return

  let konvaLayer = stage.findOne(`#layer-${layer.id}`) as KonvaLayer
  if (konvaLayer) {
    konvaLayer.destroy()
  }

  konvaLayer = new Konva.Layer({
    id: `layer-${layer.id}`,
    name: layer.name,
    visible: layer.visible,
    opacity: layer.opacity || 1
  })

  if (layer.position) {
    konvaLayer.x(layer.position.x)
    konvaLayer.y(layer.position.y)
  }

  if (layer.paths && layer.paths.length > 0) {
    layer.paths.forEach((path: DrawingPath) => {
      addPathToKonvaLayer(konvaLayer, path)
    })
  }

  stage.add(konvaLayer)
}

const createSilhouetteLayer = () => {
  if (!stage) return

  let silhouetteLayer = stage.findOne('#silhouette') as KonvaLayer
  if (!silhouetteLayer) {
    silhouetteLayer = new Konva.Layer({
      id: 'silhouette',
      name: 'silhouette',
      visible: silhouetteStore.isVisible,
      listening: false
    })
    stage.add(silhouetteLayer)
  }

  updateSilhouetteShape(silhouetteLayer)
}

const updateSilhouetteShape = (silhouetteLayer: KonvaLayer) => {
  if (!stage) return

  silhouetteLayer.destroyChildren()

  const currentSilhouette = silhouetteStore.currentSilhouette
  if (!currentSilhouette || !silhouetteStore.isVisible) return

  const colorToUse = silhouettePreviewColor.value || silhouetteStore.color

  const centerX = stage.width() / 2
  const centerY = stage.height() / 2

  const padding = 100
  const maxWidth = stage.width() - padding
  const maxHeight = stage.height() - padding

  try {
    if (currentSilhouette.isCustom) {
      createCustomImageSilhouette(silhouetteLayer, currentSilhouette, centerX, centerY, maxWidth, maxHeight, colorToUse)
    } else {
      createSVGSilhouette(silhouetteLayer, currentSilhouette, centerX, centerY, maxWidth, maxHeight, colorToUse)
    }

    silhouetteLayer.batchDraw()
  } catch (error) {
    console.error('Failed to create silhouette shape:', error)
  }
}

const createSVGSilhouette = (silhouetteLayer: KonvaLayer, silhouette: { path: string }, centerX: number, centerY: number, maxWidth: number, maxHeight: number, colorToUse: string) => {
  const parser = new DOMParser()
  const svgDoc = parser.parseFromString(silhouette.path, 'image/svg+xml')
  const pathElement = svgDoc.querySelector('path')

  if (!pathElement) return

  const pathData = pathElement.getAttribute('d')
  if (!pathData) return

  const scale = Math.min(maxWidth / 1024, maxHeight / 1024, 1)

  const silhouettePath = new Konva.Path({
    data: pathData,
    fill: colorToUse,
    x: centerX - (512 * scale),
    y: centerY - (512 * scale),
    scaleX: scale,
    scaleY: scale,
    listening: false
  })

  silhouetteLayer.add(silhouettePath)
}

const createCustomImageSilhouette = (silhouetteLayer: KonvaLayer, silhouette: { path: string; originalDimensions?: { width: number; height: number } }, centerX: number, centerY: number, maxWidth: number, maxHeight: number, colorToUse: string) => {
  const imageObj = new Image()

  imageObj.onload = () => {
    const originalWidth = silhouette.originalDimensions?.width || imageObj.width
    const originalHeight = silhouette.originalDimensions?.height || imageObj.height

    const scaleX = maxWidth / originalWidth
    const scaleY = maxHeight / originalHeight
    const scale = Math.min(scaleX, scaleY, 1)

    const finalWidth = originalWidth * scale
    const finalHeight = originalHeight * scale

    const silhouetteImage = new Konva.Image({
      image: imageObj,
      x: centerX - (finalWidth / 2),
      y: centerY - (finalHeight / 2),
      width: finalWidth,
      height: finalHeight,
      opacity: parseFloat(colorToUse.match(/[\d.]+(?=\))/)?.[0] || '0.5'),
      listening: false
    })

    silhouetteLayer.add(silhouetteImage)
    silhouetteLayer.batchDraw()
  }

  imageObj.onerror = () => {
    console.error('Failed to load custom silhouette image:', silhouette.path)
  }

  imageObj.src = silhouette.path
}

const addPathToKonvaLayer = (konvaLayer: KonvaLayer, path: DrawingPath) => {
  if (path.points && path.points.length >= 4) {
    const brushType = path.brushType || 'round'
    const brush = brushRegistry.getBrushByType(brushType)

    if (brush) {
      const context = {
        pathData: {
          id: path.id,
          points: path.points,
          smoothedPoints: path.smoothedPoints,
          smoothingMode: path.smoothingMode,
          stroke: path.stroke || '#000000',
          strokeWidth: path.strokeWidth || 1,
          lineCap: path.lineCap || 'round',
          lineJoin: path.lineJoin || 'round',
          dash: path.dash,
          opacity: path.opacity || 1,
          brushType: path.brushType || 'round',
          visualData: path.visualData,
          isThickToThin: path.isThickToThin,
          isThinToThick: path.isThinToThick,
          segmentCount: path.segmentCount
        },
        konvaLayer: konvaLayer
      }

      brush.recreateFromData(context)
    } else {
      const visualPoints = path.smoothedPoints || path.points

      const line = new Konva.Line({
        id: path.id || `path-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        points: visualPoints,
        stroke: path.stroke || '#000000',
        strokeWidth: path.strokeWidth || 1,
        opacity: path.opacity || 1,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false
      })

      konvaLayer.add(line)
    }

    const originalPath = new Konva.Line({
      id: `original-path-${path.id}`,
      name: 'original-path',
      points: path.points,
      stroke: '#ff0000',
      strokeWidth: path.strokeWidth || 1,
      opacity: 1,
      lineCap: 'round',
      lineJoin: 'round',
      visible: false,
      listening: true,
      isOriginalPath: true,
      originalStrokeWidth: path.strokeWidth || 1
    })

    konvaLayer.add(originalPath)
  }
}

const getTouchDistance = (touches: TouchList): number => {
  if (touches.length < 2) return 0

  const touch1 = touches[0]
  const touch2 = touches[1]

  if (!touch1 || !touch2) return 0

  const dx = touch1.clientX - touch2.clientX
  const dy = touch1.clientY - touch2.clientY

  return Math.sqrt(dx * dx + dy * dy)
}

const getTouchCenter = (touches: TouchList): { x: number; y: number } => {
  if (touches.length < 2) return { x: 0, y: 0 }

  const touch1 = touches[0]
  const touch2 = touches[1]

  if (!touch1 || !touch2) return { x: 0, y: 0 }

  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2
  }
}

const handleMouseDown = (e: KonvaEvent) => {
  if (!stage) return

  const mouseEvent = e.evt as MouseEvent

  if (modeManager.activeMode.value === 'delete') {
    const pos = stage.getPointerPosition()
    if (pos) {
      deleteMode.deleteObjectAt(pos.x, pos.y)
    }
    return
  } else if (modeManager.activeMode.value === 'pan') {
    panMode.startPanning(mouseEvent, stage)
    return
  } else if (modeManager.activeMode.value === 'position') {
    if (!layerPositioning.isDragging.value) {
      const pos = layerPositioning.getCanvasPosition(mouseEvent, stage)
      if (pos) {
        layerPositioning.startDragging(pos, stage)
      }
    }
    return
  } else {
    if (modeManager.activeMode.value && modeManager.activeMode.value !== 'drawing') {
      modeManager.switchToDrawingMode()
    }

    canvasEvents.handleMouseDown(mouseEvent, stage)
  }
}

const handleMouseMove = (e: KonvaEvent) => {
  if (!stage) return

  const mouseEvent = e.evt as MouseEvent

  if (modeManager.activeMode.value === 'delete') {
    return
  } else if (modeManager.activeMode.value === 'pan') {
    panMode.continuePanning(mouseEvent, stage)
    return
  } else if (modeManager.activeMode.value === 'position') {
    if (layerPositioning.isDragging.value) {
      const pos = layerPositioning.getCanvasPosition(mouseEvent, stage)
      if (pos) {
        layerPositioning.updatePosition(pos, stage)
      }
    }
  } else {
    canvasEvents.handleMouseMove(mouseEvent, stage)
  }
}

const handleMouseUp = (e: KonvaEvent) => {
  if (!stage) return

  const mouseEvent = e.evt as MouseEvent

  if (modeManager.activeMode.value === 'delete') {
    return
  } else if (modeManager.activeMode.value === 'pan') {
    panMode.endPanning()
    return
  } else if (modeManager.activeMode.value === 'position') {
    if (layerPositioning.isDragging.value) {
      layerPositioning.endDragging(stage)
    }
  } else {
    canvasEvents.handleMouseUp(mouseEvent, stage)
  }
}

const handleTouchStart = (e: KonvaEvent) => {
  if (!stage) return

  const touchEvent = e.evt as TouchEvent

  if (e.touches && e.touches.length === 2) {
    lastTouchDistance = getTouchDistance(e.touches)
    e.preventDefault()
    return
  }

  if (modeManager.activeMode.value === 'delete') {
    const pos = stage.getPointerPosition()
    if (pos) {
      deleteMode.deleteObjectAt(pos.x, pos.y)
    }
    return
  } else if (isPanMode.value) {
    panMode.startPanning(touchEvent, stage)
    return
  } else if (modeManager.activeMode.value === 'position' && layerPositioning.positioningLayerId.value && !layerPositioning.isDragging.value) {
    const pos = layerPositioning.getCanvasPosition(touchEvent, stage)
    if (pos) {
      touchEvent.preventDefault()
      layerPositioning.startDragging(pos, stage)
    }
  } else {
    if (modeManager.activeMode.value && modeManager.activeMode.value !== 'drawing') {
      modeManager.switchToDrawingMode()
    }
    canvasEvents.handleTouchStart(touchEvent, stage)
  }
}

const handleTouchMove = (e: KonvaEvent) => {
  if (!stage) return

  const touchEvent = e.evt as TouchEvent

  if (e.touches && e.touches.length === 2 && lastTouchDistance > 0) {
    const currentDistance = getTouchDistance(e.touches)
    const currentCenter = getTouchCenter(e.touches)

    if (currentDistance > 0) {
      TaperingEngine.onZoomActivity()

      const rawZoomFactor = currentDistance / lastTouchDistance
      const zoomFactor = Math.pow(rawZoomFactor, 0.5)

      const rect = canvasContainer.value?.getBoundingClientRect()
      if (rect) {
        const center = {
          x: currentCenter.x - rect.left,
          y: currentCenter.y - rect.top
        }

        canvasStore.zoom(zoomFactor, center)
      }

      lastTouchDistance = currentDistance
    }

    e.preventDefault()
    return
  }

  if (modeManager.activeMode.value === 'delete') {
    return
  } else if (isPanMode.value) {
    panMode.continuePanning(touchEvent, stage)
    return
  } else if (modeManager.activeMode.value === 'position' && layerPositioning.isDragging.value) {
    touchEvent.preventDefault()
    const pos = layerPositioning.getCanvasPosition(touchEvent, stage)
    if (pos) {
      layerPositioning.updatePosition(pos, stage)
    }
    return
  } else {
    canvasEvents.handleTouchMove(touchEvent, stage)
  }
}

const handleTouchEnd = (e: KonvaEvent) => {
  if (!stage) return

  const touchEvent = e.evt as TouchEvent

  if (!e.touches || e.touches.length < 2) {
    lastTouchDistance = 0
  }

  if (modeManager.activeMode.value === 'delete') {
    return
  } else if (isPanMode.value) {
    panMode.endPanning()
    return
  } else if (modeManager.activeMode.value === 'position' && layerPositioning.isDragging.value) {
    layerPositioning.endDragging(stage)
    return
  } else {
    canvasEvents.handleTouchEnd(touchEvent, stage)
  }
}

const handleKeyDown = (e: KeyboardEvent) => {
  canvasEvents.handleKeyDown(e)
}

const handleKeyUp = (e: KeyboardEvent) => {
  canvasEvents.handleKeyUp(e)
}

const handleWheel = (e: WheelEvent) => {
  if (!stage) return
  canvasEvents.handleWheel(e, stage)
}

const setupEventListeners = () => {
  if (!canvasContainer.value || !stage) return

  stage.on('mousedown', handleMouseDown)
  stage.on('mousemove', handleMouseMove)
  stage.on('mouseup', handleMouseUp)

  wheelHandler = handleWheel
  canvasContainer.value.addEventListener('wheel', wheelHandler, { passive: false })

  stage.on('touchstart', handleTouchStart)
  stage.on('touchmove', handleTouchMove)
  stage.on('touchend', handleTouchEnd)

  const stop1 = useEventListener(window, 'force-canvas-reinit', () => {
    if (stage) {
      initLayers()

      if (modeManager.activeMode.value === 'delete') {
        requestAnimationFrame(() => {
          deleteMode.activateDeleteMode()
        })
      }
    }
  })
  eventListenerStops.push(stop1)

  const stop2 = useEventListener(window, 'layers-changed', () => {
    if (stage) {
      initLayers()

      if (modeManager.activeMode.value === 'delete') {
        requestAnimationFrame(() => {
          deleteMode.activateDeleteMode()
        })
      }
    }
  })
  eventListenerStops.push(stop2)

  const eventBus = useEventBus()

  eventBusHandler = (data: LayerSelectedEvent) => {
    if (data.layerId) {
      const success = layerStore.setCurrentLayer(data.layerId)
      if (!success) {
        console.warn('Failed to select layer - may be locked:', data.layerId)
      }
    }
  }
  eventBus.on('layer-selected', eventBusHandler)

  const stop3 = useEventListener(document, 'keydown', handleKeyDown)
  eventListenerStops.push(stop3)
  const stop4 = useEventListener(document, 'keyup', handleKeyUp)
  eventListenerStops.push(stop4)
  const stop5 = useEventListener(document, 'visibilitychange', () => {
    if (document.hidden && canvasStore.isDrawing) {
      canvasStore.setDrawing(false)
      canvasStore.setCurrentPath(null)
      canvasStore.setBrushLines([])
    }
  })
  eventListenerStops.push(stop5)

  const stop6 = useEventListener(window, 'resize', () => {
    if (reinitTimeout) {
      clearTimeout(reinitTimeout)
    }

    reinitTimeout = setTimeout(() => {
      canvasStore.updateDimensions()
      reinitTimeout = null
    }, 100)
  })
  eventListenerStops.push(stop6)
}

const stopWatcher1 = watch(() => layerStore.layersInOrder.map(layer => ({
  id: layer.id,
  name: layer.name,
  visible: layer.visible,
  locked: layer.locked,
  order: layer.order
})), (newLayers: Array<{ id: string; name: string; visible: boolean; locked: boolean; order: number }>, oldLayers: Array<{ id: string; name: string; visible: boolean; locked: boolean; order: number }>) => {
  if (stage && newLayers.length > 0) {
    const structureChanged = JSON.stringify(newLayers) !== JSON.stringify(oldLayers)

    if (structureChanged) {
      console.log('🔄 Layer structure changed, reinitializing canvas')

      if (reinitTimeout) {
        clearTimeout(reinitTimeout)
      }

      reinitTimeout = setTimeout(async () => {
        await new Promise(resolve => requestAnimationFrame(resolve))

        initLayers()

        if (modeManager.activeMode.value === 'delete') {
          requestAnimationFrame(() => {
            deleteMode.activateDeleteMode()
          })
        }

        reinitTimeout = null
      }, 50)
    }
  }
}, { deep: true })
watcherStops.push(stopWatcher1)

const stopWatcher2 = watch(() => canvasStore.scale, (newScale: number) => {
  if (stage) {
    stage.scale({ x: newScale, y: newScale })
    stage.batchDraw()
  }
})
watcherStops.push(stopWatcher2)

const stopWatcher3 = watch(() => canvasStore.position, (newPosition: { x: number; y: number }) => {
  if (stage) {
    stage.position(newPosition)
    stage.batchDraw()
  }
})
watcherStops.push(stopWatcher3)

const stopWatcher4 = watch(() => canvasStore.backgroundColor, () => {
  updateCSSBackground()
})
watcherStops.push(stopWatcher4)

const stopWatcher5 = watch(() => silhouetteStore.currentSilhouette, () => {
  if (stage) {
    const silhouetteLayer = stage.findOne('#silhouette')
    if (silhouetteLayer) {
      updateSilhouetteShape(silhouetteLayer)
    }
  }
})
watcherStops.push(stopWatcher5)

const stopWatcher6 = watch(() => silhouetteStore.isVisible, (newVisible: boolean) => {
  if (stage) {
    const silhouetteLayer = stage.findOne('#silhouette')
    if (silhouetteLayer) {
      silhouetteLayer.visible(newVisible)
      if (newVisible) {
        updateSilhouetteShape(silhouetteLayer)
      }
      stage.batchDraw()
    }
  }
})
watcherStops.push(stopWatcher6)

const stopWatcher7 = watch(() => silhouetteStore.color, () => {
  if (stage) {
    const silhouetteLayer = stage.findOne('#silhouette')
    if (silhouetteLayer) {
      updateSilhouetteShape(silhouetteLayer)
    }
  }
})
watcherStops.push(stopWatcher7)

const stopWatcher8 = watch(() => silhouettePreviewColor.value, () => {
  if (stage) {
    const silhouetteLayer = stage.findOne('#silhouette')
    if (silhouetteLayer) {
      updateSilhouetteShape(silhouetteLayer)
    }
  }
})
watcherStops.push(stopWatcher8)

const stopWatcher9 = watch(() => backgroundPreviewColor.value, () => {
  updateCSSBackground()
})
watcherStops.push(stopWatcher9)

const stop7 = useEventListener(window, 'silhouette-color-preview', (event: CustomEvent) => {
  silhouettePreviewColor.value = event.detail.color
})
eventListenerStops.push(stop7)

const stop8 = useEventListener(window, 'silhouette-color-preview-clear', () => {
  silhouettePreviewColor.value = null
})
eventListenerStops.push(stop8)

const stop9 = useEventListener(window, 'background-color-preview', (event: CustomEvent) => {
  backgroundPreviewColor.value = event.detail.color
  updateCSSBackground()
})
eventListenerStops.push(stop9)

const stop10 = useEventListener(window, 'background-color-preview-clear', () => {
  backgroundPreviewColor.value = null
  updateCSSBackground()
})
eventListenerStops.push(stop10)

onMounted(() => {
  initCanvas()
  performance.setupPerformanceMonitoring()
})

onUnmounted(() => {
  watcherStops.forEach(stop => stop())
  watcherStops = []
  
  eventListenerStops.forEach(stop => stop())
  eventListenerStops = []

  if (reinitTimeout) {
    clearTimeout(reinitTimeout)
    reinitTimeout = null
  }

  if (canvasContainer.value && wheelHandler) {
    canvasContainer.value.removeEventListener('wheel', wheelHandler)
    wheelHandler = null
  }

  const eventBus = useEventBus()
  if (eventBusHandler) {
    eventBus.off('layer-selected', eventBusHandler)
    eventBusHandler = null
  }

  if (stage) {
    stage.off('mousedown')
    stage.off('mousemove')
    stage.off('mouseup')
    stage.off('touchstart')
    stage.off('touchmove')
    stage.off('touchend')
    stage.destroy()
    stage = null
  }

  import('@/brushes/engines/qs-brush-engine-tapering').then(({ TaperingEngine }) => {
    TaperingEngine.cleanupSharpnessTimeouts()
    TaperingEngine.clearAllCaches()
  })

  canvasStore.cleanup()
  performance.cleanupPerformanceMonitoring()
})
</script>

<style scoped>
.canvas-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;

  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.canvas-container:focus {
  outline: none;
}
</style>